import React, { useState, useEffect, useRef } from "react";
import { CrudFilter, useList } from "@refinedev/core";
import {
  Box,
  Input,
  InputGroup,
  InputRightElement,
  FormControl,
  FormLabel,
  FormErrorMessage,
  List,
  ListItem,
  Text,
  Spinner,
  VStack,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { ChevronDown, ChevronUp, X } from "lucide-react";

interface Option {
  id: string | number;
  label: string;
  value: string;
}

interface AutoCompleteProps {
  source: string;
  label?: string;
  placeholder?: string;
  value?: string; // Made optional
  onChange: (value: string) => void;
  displayField?: string; // Field to display in options (default: 'name')
  valueField?: string; // Field to use as value (default: 'id')
  filters?: CrudFilter[]; // Additional filters for the API
  debounceMs?: number; // Debounce delay for search (default: 300ms)
  minChars?: number; // Minimum characters before search (default: 1)
  maxResults?: number; // Maximum results to show (default: 10)
  disabled?: boolean;
  required?: boolean;
  error?: string;
  className?: string;
  allowClear?: boolean; // Show clear button (default: true)
  onClear?: () => void; // Callback when cleared
  sortBy?: {
    field: string;
    order: "asc" | "desc";
  };
}

export const AutoComplete: React.FC<AutoCompleteProps> = ({
  source,
  label,
  placeholder,
  value = "",
  onChange,
  displayField = "name",
  valueField = "id",
  filters = [],
  debounceMs = 300,
  minChars = 1,
  maxResults = 10,
  disabled = false,
  required = false,
  error,
  className = "",
  allowClear = true,
  onClear,
  sortBy,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Chakra UI color mode values
  const boxBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const highlightBg = useColorModeValue("blue.50", "blue.900");
  const highlightColor = useColorModeValue("blue.900", "blue.100");
  const textColor = useColorModeValue("gray.900", "white");
  const placeholderColor = useColorModeValue("gray.500", "gray.400");

  const { data, isLoading, isError } = useList({
    resource: source,
    filters: searchTerm
      ? [
          ...filters,
          {
            field: "search",
            operator: "eq",
            value: searchTerm,
          },
        ]
      : [],
    sorters: sortBy ? [{ field: sortBy.field, order: sortBy.order }] : [],
    pagination: {
      pageSize: maxResults,
      current: 1,
    },
    queryOptions: {
      enabled: searchTerm.length >= minChars || searchTerm.length === 0,
    },
  });

  // Transform data to options format
  const options: Option[] =
    data?.data?.map((item: any) => ({
      id: item[valueField],
      label: item[displayField],
      value: item[valueField].toString(),
    })) || [];

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= minChars && isOpen) {
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs, minChars, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);
  };

  const handleOptionSelect = (option: Option) => {
    setSearchTerm(option.label);
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange(option.value);
  };

  const handleClear = () => {
    setSearchTerm("");
    setIsOpen(false);
    setHighlightedIndex(-1);
    onChange("");
    onClear?.();
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setIsOpen(true);
        return;
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev > 0 ? prev - 1 : options.length - 1
        );
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleOptionSelect(options[highlightedIndex]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (
      !hasRunRef.current &&
      !searchTerm &&
      value &&
      options &&
      options.length > 0
    ) {
      const matchingOption = options.find((opt) => opt.value === value);
      if (matchingOption) {
        setSearchTerm(matchingOption.label);
        onChange(matchingOption.value);
      }
      hasRunRef.current = true;
    }
  }, [value, options]);

  // Determine if clear button should be shown
  const showClearButton = allowClear && searchTerm && !disabled;

  return (
    <Box ref={containerRef} position="relative" className={className}>
      <FormControl isInvalid={!!error} isRequired={required}>
        {/* Label */}
        {label && (
          <FormLabel fontSize="sm" fontWeight="medium">
            {label}
          </FormLabel>
        )}

        {/* Input Group */}
        <InputGroup pb={2}>
          <Input
            ref={inputRef}
            placeholder={placeholder}
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            disabled={disabled}
            autoComplete="off"
            pr={showClearButton ? "4rem" : "2.5rem"} // Adjust padding for clear button
          />
          <InputRightElement width={showClearButton ? "4rem" : "2.5rem"}>
            {showClearButton && (
              <IconButton
                aria-label="Clear selection"
                icon={<X size={14} />}
                size="xs"
                variant="ghost"
                colorScheme="gray"
                onClick={handleClear}
                mr={1}
                minW="auto"
                h="auto"
                p={1}
                _hover={{ bg: hoverBg }}
              />
            )}
            {isLoading ? (
              <Spinner size="sm" color="gray.500" />
            ) : isOpen ? (
              <ChevronUp size={16} color="#878787" />
            ) : (
              <ChevronDown size={16} color="#878787" />
            )}
          </InputRightElement>
        </InputGroup>

        {/* Error message */}
        {error && <FormErrorMessage>{error}</FormErrorMessage>}
      </FormControl>

      {/* Options dropdown */}
      {isOpen && (
        <Box
          position="absolute"
          top="100%"
          left={0}
          right={0}
          zIndex={50}
          mt={1}
          bg={boxBg}
          border="1px solid"
          borderColor={borderColor}
          borderRadius="xl"
          boxShadow="lg"
          maxH="240px"
          overflowY="auto"
        >
          {isError ? (
            <Box px={4} py={3}>
              <Text fontSize="sm" color="red.500">
                Error loading options
              </Text>
            </Box>
          ) : isLoading ? (
            <Box px={4} py={3}>
              <VStack spacing={2}>
                <Spinner size="sm" />
                <Text fontSize="sm" color={placeholderColor}>
                  Loading...
                </Text>
              </VStack>
            </Box>
          ) : options.length === 0 ? (
            <Box px={4} py={3}>
              <Text fontSize="sm" color={placeholderColor}>
                {searchTerm.length < minChars
                  ? `Type at least ${minChars} character${
                      minChars > 1 ? "s" : ""
                    } to search`
                  : "No options found"}
              </Text>
            </Box>
          ) : (
            <List spacing={0} py={1}>
              {options.map((option, index) => (
                <ListItem
                  key={option.id}
                  px={4}
                  py={3}
                  cursor="pointer"
                  fontSize="sm"
                  color={
                    index === highlightedIndex ? highlightColor : textColor
                  }
                  bg={index === highlightedIndex ? highlightBg : "transparent"}
                  _hover={{ bg: hoverBg }}
                  onClick={() => handleOptionSelect(option)}
                >
                  {option.label}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
};
